import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FaLayerGroup } from 'react-icons/fa';
import { GetAllSelectionGroupsByLocalId } from '../../../../../../../../../../../../../../crud/menu_api/selection_groups';
import GroupSearchSection from './components/GroupSearchSection/GroupSearchSection';
import GroupListSection from './components/GroupListSection/GroupListSection';
import GroupConfigurationSection from './components/GroupConfigurationSection/GroupConfigurationSection';
import GroupPreviewSection from './components/GroupPreviewSection/GroupPreviewSection';
import './ExistingGroupSelector.scss';

interface ExistingGroup {
  selection_group_id: number;
  selection_group_name: string;
  selection_group_description?: string;
  local_id: number;
  is_new: boolean;
  items_contained: Array<{
    item_id: number;
    item_name: string;
    label: string;
    is_new: boolean;
    price_modifier: number;
    delivery_price_modifier: number;
    online_price_modifier: number;
    desk_price_modifier: number;
    min_selections: number;
    max_selections: number;
  }>;
  total_items_contained: number;
  has_price_modifiers: boolean;
  has_descriptions: boolean;
  has_selection_constraints: boolean;
  configuration_complexity: {
    has_multiple_contained_items: boolean;
    has_complex_pricing: boolean;
    has_selection_rules: boolean;
  };
}

interface ExistingGroupSelectorProps {
  show: boolean;
  onHide: () => void;
  onGroupSelected: (group: any) => void;
  currentGroups: any[];
  itemName?: string;
}

const ExistingGroupSelector: React.FC<ExistingGroupSelectorProps> = ({
  show,
  onHide,
  onGroupSelected,
  currentGroups,
  itemName
}) => {
  const [existingGroups, setExistingGroups] = useState<ExistingGroup[]>([]);
  const [filteredGroups, setFilteredGroups] = useState<ExistingGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<ExistingGroup | null>(null);
  const [groupConfiguration, setGroupConfiguration] = useState({
    min_variance: 0,
    max_variance: 1,
    min_selections: 0,
    max_selections: 1,
    is_price_modifier: false
  });

  const loadExistingGroups = useCallback(async () => {
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const localId = user?.local_id || 0;
      
      const response = await GetAllSelectionGroupsByLocalId(localId);
      
      if (response.data) {
        // Filter out groups that are already added to current item
        const currentGroupIds = currentGroups.map(g => g.selection_group_id).filter(Boolean);
        const availableGroups = response.data.filter((group: ExistingGroup) => 
          !currentGroupIds.includes(group.selection_group_id)
        );
        
        setExistingGroups(availableGroups);
        setFilteredGroups(availableGroups);
      }
    } catch (error) {
      console.error('Error loading existing groups:', error);
    } finally {
      setLoading(false);
    }
  }, [currentGroups]);

  useEffect(() => {
    if (show) {
      loadExistingGroups();
    }
  }, [show, loadExistingGroups]);

  // Search functionality
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredGroups(existingGroups);
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    const filtered = existingGroups.filter(group => 
      group.selection_group_name.toLowerCase().includes(searchLower) ||
      (group.selection_group_description && group.selection_group_description.toLowerCase().includes(searchLower)) ||
      group.items_contained.some(item => item.item_name.toLowerCase().includes(searchLower))
    );
    
    setFilteredGroups(filtered);
  }, [searchTerm, existingGroups]);

  const handleGroupSelect = (group: ExistingGroup) => {
    setSelectedGroup(group);
    // Set default configuration
    setGroupConfiguration({
      min_variance: 0,
      max_variance: 1,
      min_selections: 0,
      max_selections: 1,
      is_price_modifier: false
    });
  };

  const handleConfirmSelection = () => {
    if (selectedGroup) {
      const groupToAdd = {
        ...selectedGroup,
        is_new: false,
        ...groupConfiguration
      };
      onGroupSelected(groupToAdd);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedGroup(null);
    setSearchTerm('');
    setGroupConfiguration({
      min_variance: 0,
      max_variance: 1,
      min_selections: 0,
      max_selections: 1,
      is_price_modifier: false
    });
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" className="existing-group-selector-modal-clean">
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">
            <FaLayerGroup className="me-2" />
            Seleccionar Grupo Existente
          </Modal.Title>
          {itemName && (
            <p className="form-modal-subtitle">
              Item base: <span className="current-item">{itemName}</span>
            </p>
          )}
        </div>
        <Button variant="none" className="modal-close-button" onClick={handleClose}>
          ✕
        </Button>
      </Modal.Header>
      
      <Modal.Body>
        {!selectedGroup ? (
          <div className="group-selection-container">
            <GroupSearchSection 
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
            />
            
            <GroupListSection
              loading={loading}
              filteredGroups={filteredGroups}
              selectedGroup={selectedGroup}
              onGroupSelect={handleGroupSelect}
            />
          </div>
        ) : (
          <div className="group-configuration-container">
            <GroupConfigurationSection
              groupConfiguration={groupConfiguration}
              setGroupConfiguration={setGroupConfiguration}
              selectedGroupName={selectedGroup.selection_group_name}
            />

            <GroupPreviewSection 
              selectedGroup={selectedGroup}
            />
          </div>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <div className="form-modal-footer-custom">
          {!selectedGroup ? (
            <>
              <Button 
                variant="outline-secondary" 
                onClick={handleClose}
                className="form-button-cancel"
              >
                Cancelar
              </Button>
              <Button 
                variant="primary" 
                disabled={!selectedGroup}
                onClick={handleConfirmSelection}
                className="form-button-success"
              >
                Seleccionar Grupo
              </Button>
            </>
          ) : (
            <>
              <Button 
                variant="outline-secondary" 
                onClick={() => setSelectedGroup(null)}
                className="form-button-cancel"
              >
                Volver a Lista
              </Button>
              <Button 
                variant="primary" 
                onClick={handleConfirmSelection}
                className="form-button-success"
              >
                Confirmar y Agregar
              </Button>
            </>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ExistingGroupSelector;
